import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Globe,
  MessageSquare,
  Facebook,
  Instagram,
  Linkedin,
  Search,
  Megaphone,
  Upload,
  Plus,
  CheckCircle,
  AlertCircle,
  Clock,
  RefreshCw,
  Download,
  Eye,
  Edit,
  Trash2,
  Link,
  Zap
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import { toast } from 'react-hot-toast';
import linkedInService from '../../../api/crm/linkedIn/linkedInService';
import metaService from '../../../api/crm/meta/metaService';
import googleAdsService from '../../../api/crm/googleAds/googleAdsService';
import leadService from '../../../api/crm/leads/leadService';
import { formatDate } from '../../../utils/stringUtils';
import PermissionGate from '../../../components/common/PermissionGate';

// Icon mapping
const iconMap = {
  'Globe': Globe,
  'MessageSquare': MessageSquare,
  'Facebook': Facebook,
  'Linkedin': Linkedin,
  'Search': Search,
  'Megaphone': Megaphone
};

const LeadSourcesCapture = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showConnectorModal, setShowConnectorModal] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [connectors, setConnectors] = useState([]);

  // Fetch all lead sources data
  const fetchAllSourcesData = async () => {
    try {
      setIsLoadingStatus(true);

      // Fetch lead sources data from API
      const sourcesResult = await leadService.getLeadSourcesData();

      if (sourcesResult.success && sourcesResult.sources) {
        // Map API data to connector format
        const mappedConnectors = sourcesResult.sources.map((source, index) => {
          // Format last sync date using HRMS format
          let formattedLastSync = 'Never';
          if (source.lastSyncTimestamp) {
            formattedLastSync = formatDate(source.lastSyncTimestamp, { includeTime: true });
          }

          return {
            id: index + 1,
            name: source.name,
            type: source.type,
            icon: iconMap[source.icon] || Globe,
            status: source.status || 'disconnected',
            lastSync: formattedLastSync,
            leadsCount: source.leadsCount || 0,
            description: source.description || '',
            color: source.color || 'bg-gray-500'
          };
        });

        setConnectors(mappedConnectors);
      } else {
        // Removed log
      }
    } catch (error) {
      // Removed log
    } finally {
      setIsLoadingStatus(false);
    }
  };

  // Refresh sources data after sync
  const refreshSourcesData = async () => {
    await fetchAllSourcesData();
  };

  // Handle OAuth callback from LinkedIn
  const handleOAuthCallback = async () => {
    try {
      toast.loading('Completing LinkedIn authorization...', { id: 'linkedin-callback' });
      await refreshSourcesData();
      
      const storedState = sessionStorage.getItem('linkedin_oauth_state');
      if (storedState) sessionStorage.removeItem('linkedin_oauth_state');

      toast.success('LinkedIn connected successfully!', { id: 'linkedin-callback', duration: 5000 });
      navigate('/lead_sources_capture', { replace: true });
    } catch (error) {
      toast.error(error.message || 'Failed to complete LinkedIn authorization', { id: 'linkedin-callback', duration: 6000 });
      navigate('/lead_sources_capture', { replace: true });
    }
  };

  useEffect(() => {
    fetchAllSourcesData();
  }, []);

  useEffect(() => {
    const linkedinSuccess = searchParams.get('linkedin_success');
    const linkedinError = searchParams.get('linkedin_error');
    const metaSuccess = searchParams.get('meta_success');
    const metaError = searchParams.get('meta_error');
    const googleAdsSuccess = searchParams.get('google_ads_success');
    const googleAdsError = searchParams.get('google_ads_error');

    if (linkedinSuccess === 'true') {
      handleOAuthCallback();
    } else if (linkedinError) {
      toast.error(`LinkedIn authorization failed: ${decodeURIComponent(linkedinError)}`, { duration: 6000 });
      navigate('/lead_sources_capture', { replace: true });
    }

    if (metaSuccess === 'true') {
      toast.success('Facebook/Instagram connected successfully!', { duration: 5000 });
      refreshSourcesData();
      navigate('/lead_sources_capture', { replace: true });
    } else if (metaError) {
      toast.error(`Meta authorization failed: ${decodeURIComponent(metaError)}`, { duration: 6000 });
      navigate('/lead_sources_capture', { replace: true });
    }

    if (googleAdsSuccess === 'true') {
      toast.success('Google Ads connected successfully!', { duration: 5000 });
      refreshSourcesData();
      navigate('/lead_sources_capture', { replace: true });
    } else if (googleAdsError) {
      toast.error(`Google Ads authorization failed: ${decodeURIComponent(googleAdsError)}`, { duration: 6000 });
      navigate('/lead_sources_capture', { replace: true });
    }
  }, [searchParams, navigate]);


  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'bg-green-100 text-green-800';
      case 'disconnected': return 'bg-gray-100 text-gray-800';
      case 'error': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected': return <CheckCircle className="h-4 w-4" />;
      case 'disconnected': return <Clock className="h-4 w-4" />;
      case 'error': return <AlertCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const handleConnectConnector = async (connector) => {
    if (connector.type === 'linkedin') {
      try {
        const statusResult = await linkedInService.getIntegrationStatus();
        if (statusResult.success && statusResult.connected && statusResult.tokenValid) {
          toast.error('LinkedIn is already connected with a valid token.', { id: 'linkedin-connect' });
          return;
        }

        toast.loading('Connecting to LinkedIn...', { id: 'linkedin-connect' });
        const result = await linkedInService.getAuthorizationUrl();

        if (result.success && result.authorizationUrl) {
          if (result.state) sessionStorage.setItem('linkedin_oauth_state', result.state);
          window.location.href = result.authorizationUrl;
        } else {
          toast.error(result.error || 'Failed to get LinkedIn authorization URL', { id: 'linkedin-connect' });
        }
      } catch (error) {
        toast.error(error.message || 'Failed to connect to LinkedIn', { id: 'linkedin-connect' });
      }
    } else if (connector.type === 'facebook' || connector.type === 'instagram') {
      try {
        toast.loading(`Connecting to ${connector.name}...`, { id: 'meta-connect' });
        const result = await metaService.getAuthorizationUrl();
        if (result.success && result.authorizationUrl) {
          window.location.href = result.authorizationUrl;
        } else {
          toast.error(result.error || `Failed to get ${connector.name} authorization URL`, { id: 'meta-connect' });
        }
      } catch (error) {
        toast.error('Failed to initiate connection', { id: 'meta-connect' });
      }
    } else if (connector.type === 'google_ads') {
      try {
        const statusResult = await googleAdsService.getIntegrationStatus();
        if (statusResult.success && statusResult.connected && statusResult.tokenValid) {
          toast.error('Google Ads is already connected with a valid token.', { id: 'google-ads-connect' });
          await refreshSourcesData();
          return;
        }

        toast.loading('Connecting to Google Ads...', { id: 'google-ads-connect' });
        const result = await googleAdsService.getAuthorizationUrl();

        if (result.success && result.authorizationUrl) {
          if (result.state) sessionStorage.setItem('google_ads_oauth_state', result.state);
          window.location.href = result.authorizationUrl;
        } else {
          toast.error(result.error || 'Failed to get Google Ads authorization URL', { id: 'google-ads-connect' });
        }
      } catch (error) {
        toast.error(error.message || 'Failed to connect to Google Ads', { id: 'google-ads-connect' });
      }
    } else {
      setSelectedConnector(connector);
      setShowConnectorModal(true);
    }
  };

  const handleManualEntry = () => navigate('/manual_lead_entry');

  const handleSyncConnector = async (connectorId) => {
    const connector = connectors.find(c => c.id === connectorId);
    if (connector) {
      try {
        if (connector.type === 'linkedin') {
          toast.loading('Syncing LinkedIn leads...', { id: 'linkedin-sync', duration: Infinity });
          try {
            const result = await linkedInService.syncLeads();
            if (result.success) {
              await refreshSourcesData();
              const count = result.synced || 0;
              toast.success(`LinkedIn sync completed: ${count} new lead${count !== 1 ? 's' : ''} captured`, { id: 'linkedin-sync' });
            } else {
              toast.error(result.error || 'Failed to sync LinkedIn leads', { id: 'linkedin-sync' });
            }
          } catch (e) {
            toast.error(e.message || 'LinkedIn sync failed', { id: 'linkedin-sync' });
          }
        } else if (connector.type === 'facebook' || connector.type === 'instagram') {
          toast.loading(`Starting ${connector.name} sync...`, { id: 'meta-sync' });
          try {
            const result = await metaService.syncLeads();

            if (!result.success) {
              if (result.status === 429) {
                toast.error('Sync is already running. Please wait for it to finish.', { id: 'meta-sync' });
              } else {
                toast.error(result.error || 'Failed to start sync', { id: 'meta-sync' });
              }
              return;
            }

            // Sync started — begin polling every 4 seconds until done
            toast.loading('Syncing Facebook leads... ⏳', { id: 'meta-sync' });
            let pollCount = 0;
            const maxPolls = 225; // max ~15 minutes of polling (4s interval)
            const pollInterval = setInterval(async () => {
              pollCount++;
              try {
                const statusData = await metaService.getSyncStatus();
                if (statusData.success) {
                  const { status, totalSynced, isRunning } = statusData;
                  if (status === 'pending' || isRunning) {
                    toast.loading(`Syncing Facebook leads... (${totalSynced || 0} found so far) ⏳`, { id: 'meta-sync' });
                  } else if (status === 'success') {
                    clearInterval(pollInterval);
                    await refreshSourcesData();
                    toast.success(`✅ Sync complete! ${totalSynced || 0} total leads captured.`, { id: 'meta-sync', duration: 6000 });
                  } else if (status === 'error') {
                    clearInterval(pollInterval);
                    toast.error(`Sync failed: ${statusData.lastSyncError || 'Unknown error'}`, { id: 'meta-sync', duration: 6000 });
                  }
                }
              } catch (_) { /* ignore transient poll errors */ }

              if (pollCount >= maxPolls) {
                clearInterval(pollInterval);
                await refreshSourcesData();
                toast('Sync is taking longer than expected. Check back in a few minutes.', { id: 'meta-sync', icon: '⏱️', duration: 5000 });
              }
            }, 4000);

          } catch (error) {
            if (error.response?.status === 429) {
              toast.error('Sync already in progress. Please wait.', { id: 'meta-sync' });
            } else {
              toast.error('Sync failed to start', { id: 'meta-sync' });
            }
          }

        } else if (connector.type === 'google_ads') {
          toast.loading('Syncing Google Ads leads...', { id: 'google-ads-sync', duration: Infinity });
          const result = await googleAdsService.syncLeads();
          if (result.success) {
            await refreshSourcesData();
            const count = result.synced || result?.data?.synced || 0;
            toast.success(`Google Ads sync completed: ${count} new lead${count !== 1 ? 's' : ''} captured`, { id: 'google-ads-sync' });
          } else {
            toast.error(result.error || 'Failed to sync Google Ads leads', { id: 'google-ads-sync' });
          }
        } else if (connector.type === 'website') {
          toast.success('Lead sync has been started', { id: 'website-sync', duration: 4000 });
          leadService.syncExternalLeads()
            .then(async (result) => {
              if (result.success) await refreshSourcesData();
            })
            .catch(() => {});
        } else {
          toast.success(`${connector.name} sync successful.`);
        }
      } catch (error) {
        toast.error('Unable to sync connector');
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex space-y-4 justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Lead Sources & Capture</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Configure and monitor lead capture from multiple channels</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
          <PermissionGate permission="lead_management" action="create">
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={handleManualEntry}
              className="w-full sm:w-auto bg-agrosaf-green hover:bg-agrosaf-darkGreen"
            >
              Add Lead
            </Button>
          </PermissionGate>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="space-y-4 sm:space-y-6">
          {isLoadingStatus && connectors.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
                <p className="text-gray-600">Loading lead sources...</p>
              </div>
            </div>
          ) : connectors.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No lead sources found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-4 sm:gap-6">
              {connectors.map((connector) => {
                const IconComponent = connector.icon;
                return (
                  <div key={connector.id} className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 md:p-6 hover:shadow-md transition-shadow">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3 sm:mb-4 space-y-2 sm:space-y-0">
                      <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                        <div className={`p-1.5 sm:p-2 rounded-lg ${connector.color} flex-shrink-0`}>
                          <IconComponent className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 truncate">{connector.name}</h3>
                          <p className="text-xs sm:text-sm text-gray-500 truncate mt-1">{connector.description}</p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 sm:px-2.5 rounded-full text-xs font-medium ${getStatusColor(connector.status)}`}>
                        {getStatusIcon(connector.status)}
                        <span className="ml-1 capitalize hidden md:inline">{connector.status}</span>
                      </span>
                    </div>

                    <div className="space-y-2 mb-3 sm:mb-4">
                      <div className="flex items-center justify-between text-xs sm:text-sm">
                        <span className="text-gray-500">Leads Captured:</span>
                        <span className="font-medium text-gray-900">{connector.leadsCount}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs sm:text-sm">
                        <span className="text-gray-500">Last Sync:</span>
                        <span className="font-medium text-gray-900 truncate" title={connector.lastSync}>{connector.lastSync}</span>
                      </div>
                    </div>

                    <div className="flex flex-col md:flex-row items-stretch md:items-center space-y-2 md:space-y-0 md:space-x-2">
                      {connector.status === 'connected' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          icon={<RefreshCw className="h-3 w-3 sm:h-4 sm:w-4" />}
                          onClick={() => handleSyncConnector(connector.id)}
                          className="w-full md:w-auto text-xs sm:text-sm justify-center"
                        >
                          Sync
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          icon={<Link className="h-3 w-3 sm:h-4 sm:w-4" />}
                          onClick={() => handleConnectConnector(connector)}
                          className="w-full md:w-auto bg-agrosaf-green hover:bg-agrosaf-darkGreen text-xs sm:text-sm justify-center"
                        >
                          Connect
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadSourcesCapture;
